import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Gestiona la posición del scroll al cambiar de página.
 *
 * React Router no la toca: la página nueva se monta con el scroll donde lo dejó
 * la anterior, así que si venías del pie de una página larga la siguiente
 * aparecía empezada por la mitad.
 *
 * - Al abrir un enlace (`PUSH`/`REPLACE`) se empieza por arriba.
 * - Al volver con el botón atrás (`POP`) se recupera la posición que tenías en
 *   esa página, que es lo que espera cualquiera al retroceder.
 *
 * No renderiza nada; va dentro de `BrowserRouter` para cubrir todas las rutas.
 */
export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();

  // `key` identifica cada entrada del historial, así que sirve de índice para
  // guardar una posición por visita: volver dos veces a la misma ruta son dos
  // entradas distintas y cada una recuerda la suya.
  const posiciones = useRef(new Map<string, number>());

  useEffect(() => {
    const guardar = () => {
      posiciones.current.set(key, window.scrollY);
    };

    window.addEventListener('scroll', guardar, { passive: true });

    return () => {
      window.removeEventListener('scroll', guardar);
    };
  }, [key]);

  useEffect(() => {
    // Con ancla (`/pagina#seccion`) manda el destino del enlace.
    if (hash) return;

    const guardada = posiciones.current.get(key);

    // `instant` y no `smooth`: al cambiar de página el desplazamiento animado
    // se ve como un salto raro sobre contenido que aún se está montando.
    if (navigationType === 'POP' && guardada !== undefined) {
      window.scrollTo({ top: guardada, left: 0, behavior: 'instant' });
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash, key, navigationType]);

  return null;
}
